"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import type { CatalogItem } from "@/lib/airtable";

export default function RecentlySoldCarousel({
  items,
  monochrome = true
}: {
  items: CatalogItem[];
  monochrome?: boolean;
}) {
  const [start, setStart] = useState(0);
  const visible = 4;

  const next = useCallback(() => setStart((s) => Math.min(s + 1, items.length - visible)), [items.length]);
  const prev = useCallback(() => setStart((s) => Math.max(s - 1, 0)), []);

  useEffect(() => {
    if (items.length <= visible) return;
    const t = setInterval(next, 3500);
    return () => clearInterval(t);
  }, [next, items.length]);

  const shown = items.slice(start, start + visible);

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {shown.map((item) => (
          <div key={item.id} className="relative overflow-hidden rounded-sm bg-white">
            <div className="relative aspect-square overflow-hidden">
              <Image
                src={item.pictureUrl}
                alt={item.itemName}
                fill
                className={`object-contain${monochrome ? " opacity-70 grayscale" : ""}`}
                sizes="(max-width: 640px) 50vw, 25vw"
              />
              <div className="absolute inset-0 flex items-end p-2">
                <span className="rounded-sm bg-slate-800/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                  Sold
                </span>
              </div>
            </div>
            <div className="py-2">
              <p className="truncate text-xs text-slate-600">{item.itemName}</p>
            </div>
          </div>
        ))}
      </div>
      {items.length > visible && (
        <div className="mt-4 flex justify-center gap-3">
          <button type="button" onClick={prev} disabled={start === 0} className="rounded-sm border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition hover:border-slate-400 disabled:opacity-30">←</button>
          <button type="button" onClick={next} disabled={start >= items.length - visible} className="rounded-sm border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition hover:border-slate-400 disabled:opacity-30">→</button>
        </div>
      )}
    </div>
  );
}
